/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type ComponentPropsWithoutRef, forwardRef } from 'react';

import { cssMap, cx, jsx } from '@compiled/react';

import type { IconButtonProps } from '@atlaskit/button/new';
import mergeRefs from '@atlaskit/ds-lib/merge-refs';
import Pressable, { type PressableProps } from '@atlaskit/primitives/pressable';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import VisuallyHidden from '@atlaskit/visually-hidden';

export const themedButtonBackground = '--ds-top-bar-button-background';
export const themedButtonBackgroundHovered = '--ds-top-bar-button-background-hovered';
export const themedButtonBackgroundPressed = '--ds-top-bar-button-background-pressed';
export const themedButtonBorder = '--ds-top-bar-button-border';

export const themedButtonPrimaryText = '--ds-top-bar-button-primary-text';
export const themedButtonPrimaryBackground = '--ds-top-bar-button-primary-background';
export const themedButtonPrimaryBackgroundHovered =
	'--ds-top-bar-button-primary-background-hovered';
export const themedButtonPrimaryBackgroundPressed =
	'--ds-top-bar-button-primary-background-pressed';

export const themedButtonSelectedText = '--ds-top-bar-button-selected-text';
export const themedButtonSelectedBackground = '--ds-top-bar-button-selected-background';
export const themedButtonSelectedBackgroundHovered =
	'--ds-top-bar-button-selected-background-hovered';
export const themedButtonSelectedBackgroundPressed =
	'--ds-top-bar-button-selected-background-pressed';
export const themedButtonSelectedBorder = '--ds-top-bar-button-selected-border';

export const themedButtonDisabledText = '--ds-top-bar-button-disabled-text';
export const themedButtonDisabledBackground = '--ds-top-bar-button-disabled-background';

type ButtonAppearance = 'default' | 'primary' | 'subtle';

/**
 * Props shared by all button variants.
 */
interface SharedButtonProps
	extends Omit<
			ComponentPropsWithoutRef<'button'>,
			// Handled by `isDisabled`
			| 'disabled'
			// Has a custom handler for analytics
			| 'onClick'
			// Not supported
			| 'type'
			| 'style'
			| 'className'
		>,
		Pick<
			PressableProps,
			| 'onClick'
			| 'interactionName'
			| 'analyticsContext'
			// Both `testId` and `data-testid` are specified intentionally
			// Because the `data-testid` has type `never` so people don't use it accidentally
			| 'testId'
			| 'data-testid'
			| 'isDisabled'
		> {
	/**
	 * The button style variation.
	 */
	appearance?: ButtonAppearance;
	/**
	 * Indicates that the button is selected.
	 */
	isSelected?: boolean;
}

/**
 * Props for the internal-only base button.
 */
interface BaseThemedButtonProps extends SharedButtonProps {
	shape?: 'default' | 'square';
	children: React.ReactNode;
}

/**
 * Props for the exported themed text button.
 */
export interface ThemedButtonProps extends SharedButtonProps {
	/**
	 * Places an icon within the button, before the button's text.
	 */
	iconBefore?: IconButtonProps['icon'];
	/**
	 * Text content to be rendered in the button.
	 */
	children: React.ReactNode;
}

/**
 * Props for the exported themed icon button.
 */
export interface ThemedIconButtonProps extends SharedButtonProps {
	// Icon button doesn't support children
	children?: never;
	// Prevent duplicate labels being added.
	'aria-label'?: never;
	/**
	 * Provide an accessible label, often used by screen readers.
	 */
	label: React.ReactNode;
	/**
	 * Places an icon within the button.
	 */
	icon: IconButtonProps['icon'];
	/**
	 * Props passed down to the Tooltip component.
	 */
	tooltip?: IconButtonProps['tooltip'];
}

// TODO: use codegen to ensure these don't get stale
const styles = cssMap({
	root: {
		display: 'flex',
		gap: token('space.075'),
		alignItems: 'center',
		justifyContent: 'center',
		font: token('font.body'),
		height: '2.2857142857142856em',
		paddingBlock: token('space.0'),
		borderRadius: token('border.radius.100', '3px'),
		transition: 'background 0.1s ease-out',
		position: 'relative',
	},
	border: {
		'&::after': {
			content: '""',
			borderRadius: 'inherit',
			borderStyle: 'solid',
			borderWidth: token('border.width'),
			position: 'absolute',
			inset: token('space.0'),
		},
	},
	selected: {
		color: `var(${themedButtonSelectedText})`,
		background: `var(${themedButtonSelectedBackground})`,
		'&:hover': {
			background: `var(${themedButtonSelectedBackgroundHovered})`,
		},
		'&:active': {
			background: `var(${themedButtonSelectedBackgroundPressed})`,
		},
		'&::after': {
			borderColor: `var(${themedButtonSelectedBorder})`,
		},
	},
	disabled: {
		color: `var(${themedButtonDisabledText})`,
		background: `var(${themedButtonDisabledBackground})`,
		'&:hover': {
			background: `var(${themedButtonDisabledBackground})`,
		},
		'&:active': {
			background: `var(${themedButtonDisabledBackground})`,
		},
		'&::after': {
			borderColor: 'transparent',
		},
	},
});

const shapeStyles = cssMap({
	/**
	 * The regular rectangular button shape
	 */
	default: {
		paddingInline: token('space.150'),
	},
	/**
	 * A square button shape, used for icon buttons
	 */
	square: {
		width: '2.2857142857142856em',
		paddingInline: token('space.0'),
		lineHeight: 0,
	},
});

const appearanceStyles = cssMap({
	default: {
		color: 'currentColor',
		background: `var(${themedButtonBackground})`,
		'&:hover': {
			background: `var(${themedButtonBackgroundHovered})`,
		},
		'&:active': {
			background: `var(${themedButtonBackgroundPressed})`,
		},
		'&::after': {
			borderColor: `var(${themedButtonBorder})`,
		},
	},
	subtle: {
		color: 'currentColor',
		background: `var(${themedButtonBackground})`,
		'&:hover': {
			background: `var(${themedButtonBackgroundHovered})`,
		},
		'&:active': {
			background: `var(${themedButtonBackgroundPressed})`,
		},
	},
	primary: {
		color: `var(${themedButtonPrimaryText})`,
		background: `var(${themedButtonPrimaryBackground})`,
		'&:hover': {
			background: `var(${themedButtonPrimaryBackgroundHovered})`,
		},
		'&:active': {
			background: `var(${themedButtonPrimaryBackgroundPressed})`,
		},
	},
});

const BaseThemedButton = forwardRef<HTMLButtonElement, BaseThemedButtonProps>(
	function BaseThemedButton(
		{ appearance = 'default', shape = 'default', isSelected, isDisabled, children, ...props },
		ref,
	) {
		const hasBorder = appearance === 'default' || isSelected;

		return (
			<Pressable
				{...props}
				type="button"
				ref={ref}
				// @ts-expect-error
				// eslint-disable-next-line @compiled/no-suppress-xcss
				xcss={cx(
					styles.root,
					shapeStyles[shape],
					hasBorder && styles.border,
					appearanceStyles[appearance],
					isSelected && styles.selected,
					isDisabled && styles.disabled,
				)}
				isDisabled={isDisabled}
			>
				{children}
			</Pressable>
		);
	},
);

const textButtonStyles = cssMap({
	iconBefore: {
		lineHeight: 0,
	},
	text: {
		fontWeight: token('font.weight.medium'),
	},
});

/**
 * __Themed button__
 *
 * A themed button for the top bar.
 */
export const ThemedButton = forwardRef<HTMLButtonElement, ThemedButtonProps>(function ThemedButton(
	{ iconBefore: IconBefore, children, ...props },
	ref,
) {
	return (
		<BaseThemedButton {...props} ref={ref}>
			{IconBefore && (
				<span css={textButtonStyles.iconBefore}>
					<IconBefore label="" color="currentColor" />
				</span>
			)}
			<span css={textButtonStyles.text}>{children}</span>
		</BaseThemedButton>
	);
});

/**
 * __Themed icon button__
 *
 * A themed icon button for the top bar.
 */
export const ThemedIconButton = forwardRef<HTMLButtonElement, ThemedIconButtonProps>(
	function ThemedIconButton({ icon: Icon, label, onClick, tooltip, ...props }, ref) {
		return (
			<Tooltip {...tooltip} content={tooltip?.content ?? label}>
				{(triggerProps) => (
					/**
					 * The `aria-describedby` from `triggerProps` is intentionally not passed down,
					 * because it would cause double announcements with the `VisuallyHidden` label.
					 *
					 * The `@atlaskit/button` IconButton uses the same approach.
					 */
					<BaseThemedButton
						{...props}
						shape="square"
						ref={mergeRefs([ref, triggerProps.ref])}
						onClick={(event, analyticsEvent) => {
							onClick?.(event, analyticsEvent);
							triggerProps?.onClick?.(event);
						}}
						onMouseOver={(e) => {
							triggerProps.onMouseOver?.(e);
							props.onMouseOver?.(e);
						}}
						onMouseOut={(e) => {
							triggerProps.onMouseOut?.(e);
							props.onMouseOut?.(e);
						}}
						onMouseMove={(e) => {
							triggerProps.onMouseMove?.(e);
							props.onMouseMove?.(e);
						}}
						onMouseDown={(e) => {
							triggerProps.onMouseDown?.(e);
							props.onMouseDown?.(e);
						}}
						onFocus={(e) => {
							triggerProps.onFocus?.(e);
							props.onFocus?.(e);
						}}
						onBlur={(e) => {
							triggerProps.onBlur?.(e);
							props.onBlur?.(e);
						}}
					>
						<Icon label="" color="currentColor" />
						<VisuallyHidden>{label}</VisuallyHidden>
					</BaseThemedButton>
				)}
			</Tooltip>
		);
	},
);
